import { supabase, getUid } from "./supabase";
import { sanitizeSettings } from "./settings";

/* Email the signed-out user a password-reset link. The link returns them to
   /reset (which must be in your Supabase Redirect URLs allow-list), where
   they choose a new password. */
export async function requestPasswordReset(email) {
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset`,
  });
}

/* Change (or first-claim) the signed-in user's username via the server RPC,
   which enforces format, the 30-day rate limit, availability, and the 30-day
   reservation of the old name. Returns the server's jsonb verdict:
   { ok:true, username } or { ok:false, error, days_left? }. */
export async function changeUsername(newName) {
  const { data, error } = await supabase.rpc("change_username", {
    new_name: newName,
  });
  if (error) {
    console.warn("username change failed:", error.message);
    return { ok: false, error: "server" };
  }
  return data;
}

/* Mark the onboarding tour as seen (finished or skipped) so it won't auto-run
   again. Optimistic — a failure just means it may re-prompt next session. */
export async function markOnboarded() {
  const uid = await getUid();
  if (!uid) return { ok: false };
  const { error } = await supabase
    .from("profiles")
    .update({ onboarded: true })
    .eq("id", uid);
  if (error) {
    console.warn("mark onboarded failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/* Permanently delete the current account via the delete-account Edge
   Function, then sign out locally. Works for both email and Google users.
   Returns { ok, error }. */
export async function deleteAccount() {
  const { data, error } = await supabase.functions.invoke("delete-account");
  if (error) return { ok: false, error: error.message };
  if (data?.error) return { ok: false, error: data.error };
  await supabase.auth.signOut();
  return { ok: true };
}

/* Save the player's current Rules as their personal default — sanitized, on
   their own profile row — so the trainer seeds from it on future visits. */
export async function saveDefaultSettings(settings) {
  const uid = await getUid();
  if (!uid) return { ok: false, error: "not_signed_in" };
  const clean = sanitizeSettings(settings);
  if (!clean) return { ok: false, error: "invalid" };
  const { error } = await supabase
    .from("profiles")
    .update({ default_settings: clean })
    .eq("id", uid);
  if (error) {
    console.warn("save default settings failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}

/* Clear the saved default so the app's built-in defaults apply again. */
export async function clearDefaultSettings() {
  const uid = await getUid();
  if (!uid) return { ok: false, error: "not_signed_in" };
  const { error } = await supabase
    .from("profiles")
    .update({ default_settings: null })
    .eq("id", uid);
  if (error) {
    console.warn("clear default settings failed:", error.message);
    return { ok: false, error: error.message };
  }
  return { ok: true };
}
